import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { Home } from "./pages/Home";
import { SearchPage } from "./pages/Search";
import { ProfilePage } from "./pages/Profile";
import { RangosPage } from "./pages/Rangos";
import { ComoFuncionaPage } from "./pages/ComoFunciona";
import { AsociacionesPage } from "./pages/Asociaciones";
import { ContactoPage } from "./pages/Contacto";
import { ReglasMarcaPage } from "./pages/ReglasMarca";
import { OfertaAcademicaPage } from "./pages/OfertaAcademica";
import { MembresiaPage } from "./pages/Membresia";
import { LoginPage } from "./pages/Login";
import { RegisterPage } from "./pages/Register";
import { DashboardPage } from "./pages/Dashboard";
import { 
  AdminLayout, 
  AdminDashboard, 
  AdminProfessionals, 
  AdminDocuments, 
  AdminMemberships, 
  AdminCourses,
  AdminEnrollments,
  AdminContactMessages,
  AnalyticsDashboard
} from "./pages/admin";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function PublicOnlyRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function MainLayout({ children }) {
  return (
    <div className="flex flex-col min-h-screen bg-[#111111]">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  useEffect(() => {
    const seedDatabase = async () => {
      try {
        await fetch(`${API}/seed`, { method: 'POST' });
      } catch (error) {
        console.log('Database may already be seeded');
      }
    };
    seedDatabase();
  }, []);

  return (
    <Routes>
      {/* Admin Routes - No Navbar/Footer */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="profesionales" element={<AdminProfessionals />} />
        <Route path="documentos" element={<AdminDocuments />} />
        <Route path="membresias" element={<AdminMemberships />} />
        <Route path="cursos" element={<AdminCourses />} />
        <Route path="verificaciones" element={<AdminEnrollments />} />
        <Route path="mensajes" element={<AdminContactMessages />} />
        <Route path="analytics" element={<AnalyticsDashboard />} />
      </Route>

      {/* Public Routes with Layout */}
      <Route path="/" element={<MainLayout><Home /></MainLayout>} />
      <Route path="/buscar" element={<MainLayout><SearchPage /></MainLayout>} />
      <Route path="/profesional/:id" element={<MainLayout><ProfilePage /></MainLayout>} />
      <Route path="/rangos" element={<MainLayout><RangosPage /></MainLayout>} />
      <Route path="/como-funciona" element={<MainLayout><ComoFuncionaPage /></MainLayout>} />
      <Route path="/asociaciones" element={<MainLayout><AsociacionesPage /></MainLayout>} />
      <Route path="/contacto" element={<MainLayout><ContactoPage /></MainLayout>} />
      <Route path="/reglas-marca" element={<MainLayout><ReglasMarcaPage /></MainLayout>} />
      <Route path="/oferta-academica" element={<MainLayout><OfertaAcademicaPage /></MainLayout>} />
      <Route path="/membresia" element={<MainLayout><MembresiaPage /></MainLayout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={
        <MainLayout>
          <PublicOnlyRoute><LoginPage /></PublicOnlyRoute>
        </MainLayout>
      } />
      <Route path="/registro" element={
        <MainLayout>
          <PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>
        </MainLayout>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <MainLayout>
          <ProtectedRoute><DashboardPage /></ProtectedRoute>
        </MainLayout>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
